import { stripHtmlToPlainText } from '@/utilities/formatCourseTemplateActivityPreview.js';

/**
 * Turns the three inbox list payloads into one row shape, so every tab renders through the same
 * table: title + subtitle, author, a type tag, a date, read state and the item's own page.
 *
 * @typedef {Object} InboxRow
 * @property {string} key unique across types (announcements and updates share ids)
 * @property {number} id
 * @property {string} readableType the read-receipt model class
 * @property {string} title
 * @property {string} subtitle
 * @property {Object|null} to router location of the item, null when it cannot be opened
 * @property {Object|null} from the author, with only the exposed person fields
 * @property {boolean} fromYou
 * @property {{label: string, severity: string}} type
 * @property {string|null} date ISO timestamp
 * @property {boolean} read
 * @property {boolean} important
 */

const SNIPPET_LENGTH = 90;

/**
 * @param {string|null|undefined} html
 * @returns {string} plain text, clipped to a single-line preview
 */
export function snippet(html) {
    // Tags become spaces first so adjacent blocks keep a word break; the helper then decodes entities.
    const text = stripHtmlToPlainText((html ?? '').replace(/<[^>]*>/g, ' '));

    return text.length > SNIPPET_LENGTH ? `${text.slice(0, SNIPPET_LENGTH).trimEnd()}…` : text;
}

function courseLabel(course) {
    return course?.prepended_course_code_title || course?.title || '';
}

function joinParts(...parts) {
    return parts.filter(Boolean).join(' · ');
}

/**
 * @returns {InboxRow}
 */
export function announcementRow(row) {
    const isUpdate = Boolean(row.activity_id);

    return {
        key: `${isUpdate ? 'update' : 'announcement'}-${row.id}`,
        id: row.id,
        readableType: isUpdate ? 'App\\Models\\ActivityUpdate' : 'App\\Models\\CourseAnnouncement',
        title: row.title || 'Untitled',
        subtitle: courseLabel(row.course),
        to: {
            name: 'announcement-view',
            params: {
                courseId: row.course_id,
                type: isUpdate ? 'activity-update' : 'course-announcement',
                announcementId: row.id,
            },
        },
        from: row.creator ?? null,
        fromYou: false,
        type: isUpdate
            ? { label: 'Activity update', severity: 'info' }
            : { label: 'Announcement', severity: 'secondary' },
        date: row.published_at ?? row.created_at ?? null,
        read: Boolean(row.read),
        important: Boolean(row.important),
    };
}

/**
 * A reply has no title of its own: it is titled after its thread and opens the thread.
 *
 * @param {Object} row
 * @param {number|null} currentUserId
 * @returns {InboxRow}
 */
export function discussionRow(row, currentUserId) {
    const isReply = row.discussion_type === 'reply';
    const thread = isReply ? row.thread : row;
    const threadTitle = thread?.title || 'Untitled discussion';
    const canOpen = Boolean(thread?.id && thread?.course_id && thread?.activity_id);

    return {
        key: `${isReply ? 'reply' : 'thread'}-${row.id}`,
        id: row.id,
        readableType: isReply ? 'App\\Models\\DiscussionReply' : 'App\\Models\\DiscussionThread',
        title: isReply ? `Re: ${threadTitle}` : threadTitle,
        subtitle: joinParts(snippet(row.body) && `“${snippet(row.body)}”`, row.course?.title),
        to: canOpen
            ? {
                  name: 'discussion-thread-view',
                  params: { courseId: thread.course_id, activityId: thread.activity_id, id: thread.id },
              }
            : null,
        from: row.user ?? null,
        fromYou: Boolean(currentUserId && row.user?.id === currentUserId),
        type: isReply ? { label: 'Reply', severity: 'info' } : { label: 'Your thread', severity: 'secondary' },
        date: row.created_at ?? null,
        read: Boolean(row.read),
        important: false,
    };
}

/**
 * @returns {InboxRow}
 */
export function messageRow(row) {
    return {
        key: `message-${row.id}`,
        id: row.id,
        readableType: 'App\\Models\\Message',
        title: row.title || row.subject || 'Untitled message',
        subtitle: joinParts(row.title && row.subject, snippet(row.body)),
        to: { name: 'message-view', params: { messageId: row.id } },
        from: row.sender ?? null,
        fromYou: false,
        type: { label: 'Message', severity: 'secondary' },
        date: row.created_at ?? null,
        read: Boolean(row.read),
        important: false,
    };
}

/**
 * @param {'announcements'|'discussions'|'messages'} section
 * @param {Array<Object>} rows
 * @param {number|null} currentUserId
 * @returns {Array<InboxRow>}
 */
export function toInboxRows(section, rows, currentUserId = null) {
    const map = {
        announcements: announcementRow,
        discussions: row => discussionRow(row, currentUserId),
        messages: messageRow,
    };

    return (rows ?? []).map(map[section]);
}
